import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Table, Spin, Select, Space, Form, Modal, message } from "antd";
import moment from "moment";
import styled from "@emotion/styled";
import { CSVLink } from "react-csv";
// Redux
import { connect } from "react-redux";
import { getUserInfo, getClientid } from "redux/selectors";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import COLORS from "common/Colors";
import Button from "atoms/Button";
import Icon from "atoms/Icon";
import ViewMore from "organisms/ViewMore";
import RejectReasonModal from "molecules/RejectReasonModal";
import { ButtonOutlined } from "reusableComponent/UIButtons";

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .normal-column {
    min-width: 250px;
    white-space: normal;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
`;

const StyledApprovalSearchForm = styled(Form)`
  .ant-form-item {
    display: inline-block;
    margin-bottom: 5px;
  }
  .ant-select-selector {
    min-width: 180px;
    margin-bottom: 10px;
    width: 180px;
  }
`;

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Declined", label: "Declined" },
];

const iconStyle = { fontSize: "12px", cursor: "pointer" };
const disabledIcon = {
  fontSize: "12px",
  cursor: "not-allowed",
  opacity: "0.3",
};

function CRMApproval({ userInfo, ClientId }) {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const csvLinkEl = useRef();

  const [clientList, setClientList] = useState([]);
  const [displayTaskLoader, setDisplayTaskLoader] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("Pending");
  const [CSVClientList, setCSVClientList] = useState([]);

  const [selectedClient, setSelectedClient] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    getCrmApprovalList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCrmApprovalList = async () => {
    setDisplayTaskLoader(true);
    const { code, data = [] } = await CALL_API(
      `insyts-crm-clients/approvals`,
      "get"
    );
    setDisplayTaskLoader(false);
    if (code === STATUS_CODE.SUCCESS) {
      setClientList(data);
    } else {
      message.error(`Something went wrong. Please try again.`);
    }
  };

  const filteredList = clientList.filter(
    (item) => (item.approvalStatus || "Pending") === approvalStatus
  );

  const openApproveModal = (record) => {
    setSelectedClient(record);
    setIsApproveModalOpen(true);
  };

  const openDeclineModal = (record) => {
    setSelectedClient(record);
    setIsDeclineModalOpen(true);
  };

  const submitApprovalStatus = async (status, declineReason = "") => {
    setActionLoading(true);
    const { code } = await CALL_API(
      `insyts-crm-clients/approval/${selectedClient?.clientId}`,
      "patch",
      {
        approvalStatus: status,
        declineReason,
        approvedByMailId: userInfo?.userid,
        approvedByName: userInfo?.name,
      }
    );
    setActionLoading(false);
    setIsApproveModalOpen(false);
    setIsDeclineModalOpen(false);
    if (code === STATUS_CODE.SUCCESS) {
      message.success(`Client has been ${status.toLowerCase()}`);
      getCrmApprovalList();
    } else {
      message.error(`Something went wrong. Please try again.`);
    }
  };

  const handleApproveOk = () => submitApprovalStatus("Approved");
  const handleDeclineClose = (reason) => {
    if (reason === false) {
      setIsDeclineModalOpen(false);
      return;
    }
    submitApprovalStatus("Declined", reason);
  };

  const columns = [
    {
      title: "Client ID",
      dataIndex: "clientId",
      key: "clientId",
      width: "10%",
    },
    {
      title: "Registered Name",
      dataIndex: "registeredName",
      key: "registeredName",
      width: "18%",
      className: "normal-column",
    },
    {
      title: "Managing Office",
      dataIndex: "managingOffice",
      key: "managingOffice",
      width: "12%",
    },
    {
      title: "Relationship Type",
      dataIndex: "relationshipType",
      key: "relationshipType",
      width: "12%",
    },
    {
      title: "Primary Contact Email",
      dataIndex: "primaryContactEmail",
      key: "primaryContactEmail",
      width: "15%",
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: "10%",
    },
    {
      title: "Created On",
      dataIndex: "createdon",
      key: "createdon",
      width: "10%",
      render: (value) => (value ? moment(value).format("DD MMM YYYY") : "-"),
    },
  ];

  if (approvalStatus === "Approved") {
    columns.push(
      {
        title: "Approved By",
        dataIndex: "approvedByName",
        key: "approvedByName",
        width: "10%",
        render: (value) => value || "-",
      },
      {
        title: "Approved On",
        dataIndex: "approvedOn",
        key: "approvedOn",
        width: "10%",
        render: (value) => (value ? moment(value).format("DD MMM YYYY") : "-"),
      }
    );
  }

  if (approvalStatus === "Declined") {
    columns.push(
      {
        title: "Declined By",
        dataIndex: "approvedByName",
        key: "approvedByName",
        width: "10%",
        render: (value) => value || "-",
      },
      {
        title: "Decline Reason",
        dataIndex: "declineReason",
        key: "declineReason",
        width: "20%",
        className: "normal-column",
        render: (reason) => <ViewMore value={reason} />,
      }
    );
  }

  if (approvalStatus === "Pending") {
    columns.push({
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Icon
            name="tick"
            style={actionLoading ? disabledIcon : iconStyle}
            onClick={(e) => {
              if (actionLoading) return;
              e.stopPropagation();
              openApproveModal(record);
            }}
          />
          <Icon
            name="wrong"
            style={actionLoading ? disabledIcon : iconStyle}
            onClick={(e) => {
              if (actionLoading) return;
              e.stopPropagation();
              openDeclineModal(record);
            }}
          />
        </div>
      ),
    });
  }

  const CSVHeaders = [
    { label: "Client ID", key: "clientId" },
    { label: "Registered Name", key: "registeredName" },
    { label: "Managing Office", key: "managingOffice" },
    { label: "Relationship Type", key: "relationshipType" },
    { label: "Primary Contact Email", key: "primaryContactEmail" },
    { label: "Created By", key: "createdByName" },
    { label: "Created On", key: "createdon" },
    { label: "Approved/Declined By", key: "approvedByName" },
    { label: "Decline Reason", key: "declineReason" },
  ];

  const prepareCSV = () => {
    if (filteredList.length > 0) {
      setCSVClientList(filteredList);
      setTimeout(() => {
        csvLinkEl.current.link.click();
      });
    } else {
      message.error(`No data available to download.`);
    }
  };

  const paginationOptions = {
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    pageSize: 20,
    showSizeChanger: false,
  };

  return (
    <>
      <Row className="page-title">
        <Col xs={24} sm={19} md={19} className="page-title-head" ref={containerRef}>
          <Space>
            <StyledApprovalSearchForm form={form} name="crm_approval_form" layout="inline">
              <Form.Item name="approval_status">
                <Select
                  showSearch
                  placeholder="Select..."
                  value={approvalStatus}
                  options={STATUS_OPTIONS}
                  onChange={setApprovalStatus}
                  getPopupContainer={() => containerRef.current}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </StyledApprovalSearchForm>
          </Space>
        </Col>
        <Col xs={{ order: 1, span: 24 }} lg={{ order: 2, span: 4 }} className="align-right">
          <ButtonOutlined onClick={prepareCSV}>Export CSV file</ButtonOutlined>
          <CSVLink
            header={CSVHeaders}
            data={CSVClientList}
            filename={`crm-approval-${approvalStatus}.csv`}
            asyncOnClick={true}
            ref={csvLinkEl}
          />
        </Col>
      </Row>
      <Spin spinning={displayTaskLoader}>
        <StyledTable
          dataSource={filteredList}
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey="clientId"
          pagination={filteredList.length > 20 ? paginationOptions : false}
        />
      </Spin>

      <Modal
        title="Approve client"
        open={isApproveModalOpen}
        onOk={handleApproveOk}
        onCancel={() => setIsApproveModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => setIsApproveModalOpen(false)}>
            No
          </Button>,
          <Button key="submit" type="primary" loading={actionLoading} onClick={handleApproveOk}>
            Yes
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to approve client "{selectedClient?.registeredName}"?
        </p>
      </Modal>

      <RejectReasonModal isOpenModal={isDeclineModalOpen} closeModal={handleDeclineClose} />
    </>
  );
}

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
  ClientId: getClientid(state),
});

export default connect(mapStateToProps, null)(CRMApproval);
